import { z } from 'zod';

/**
 * The only place the admin app reads import.meta.env, apart from
 * `import.meta.env.DEV` guarding dev-only code (login.tsx). That one is read
 * inline: Vite replaces it with false before bundling, so a production build
 * drops the dev code and everything it imports. Behind an imported constant,
 * the chunks were already split and kept the dev admin's email. Fails fast at boot.
 */
const EnvSchema = z.object({
  /**
   * Which deployment this is, independent of the build mode (D29).
   *
   * `playground` is a production build against throwaway data, so it must
   * say so in the shell. Nothing may key off `import.meta.env.PROD` for that.
   */
  VITE_APP_ENV: z.enum(['local', 'playground', 'production']).default('local'),
  /**
   * `remote` points the browser at same-origin /api/*, which the Vite dev
   * proxy sends to the playground, so back office work needs no API and no
   * Docker (D29). `direct` is the normal two-host setup (D18).
   */
  VITE_API_MODE: z.enum(['direct', 'remote']).default('direct'),
  VITE_API_URL: z.string().url().default('http://api.lvh.me:4000'),
  VITE_APP_ORIGIN: z.string().url().default('http://app.lvh.me:3000'),
});

export type Env = z.infer<typeof EnvSchema>;

/**
 * An unset build argument or Railway variable arrives as an empty string, not
 * as undefined, and an empty string is not a valid URL. Treat both as absent
 * so the schema defaults apply.
 */
function orUndefined(value: string | undefined): string | undefined {
  return value === undefined || value.trim() === '' ? undefined : value;
}

const parsed = EnvSchema.safeParse({
  VITE_APP_ENV: orUndefined(import.meta.env.VITE_APP_ENV),
  VITE_API_MODE: orUndefined(import.meta.env.VITE_API_MODE),
  VITE_API_URL: orUndefined(import.meta.env.VITE_API_URL),
  VITE_APP_ORIGIN: orUndefined(import.meta.env.VITE_APP_ORIGIN),
});

if (!parsed.success) {
  const lines = parsed.error.issues.map((issue) => `  ${issue.path.join('.')}: ${issue.message}`);
  throw new Error(`Invalid admin configuration:\n${lines.join('\n')}`);
}

export const env: Env = parsed.data;

/**
 * Where the back office sends API calls. Remote mode uses the same origin so
 * the session cookie is first-party on admin.lvh.me; the Vite dev proxy
 * forwards /api/* to the playground.
 */
export const apiBaseUrl = env.VITE_API_MODE === 'remote' ? '/api' : env.VITE_API_URL;
